/**
 * <AskPanel country week platform brand category />
 *
 * A small, collapsible question box grounded on the Business Review of the week and scope shown on the page
 * (POST /api/ai/copilot with that scope). Answers are rendered as markdown without images or live links, so
 * nothing echoed from uploaded data can send data out. Conversation history stays per user (session id).
 */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ChevronDown, ChevronUp, Loader2, MessageSquare, Send } from 'lucide-react';
import { getAuthHeaders } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { errorMessage } from '../LoadError';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
const DISALLOWED = ['img'];
const COMPONENTS = { a: ({ children, href }) => <span title={href}>{children}{href ? ` (${href})` : ''}</span> };
const SUGGESTIONS = ['Why did revenue change this week?', 'Which SKUs should I reorder first?', 'Where is ad spend wasted?'];

export const AskPanel = ({ country, week, platform, brand, category }) => {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const send = async text => {
    const question = (text || input).trim();
    if (!question || busy) return;
    setInput('');
    setMessages(prev => [...prev, { id: `u${Date.now()}`, role: 'user', text: question }]);
    setBusy(true);
    try {
      const res = await axios.post(`${API}/ai/copilot`, { message: question, session_id: sessionId, country, week, platform, brand, category },
        { headers: getAuthHeaders(), timeout: 90000 });
      setSessionId(res.data.session_id);
      setMessages(prev => [...prev, { id: `a${Date.now()}`, role: 'assistant', text: res.data.response }]);
    } catch (error) {
      setMessages(prev => [...prev, { id: `e${Date.now()}`, role: 'assistant', text: `The question could not be answered: ${errorMessage(error)}` }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-xl border border-gray-200 bg-white" aria-labelledby="ask-title" data-testid="review-ask-panel">
      <button type="button" onClick={() => setOpen(value => !value)} aria-expanded={open}
        className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">
        <span className="flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-gray-500" aria-hidden="true" />
          <span id="ask-title" className="text-sm font-semibold text-gray-900">Ask about this week</span>
          <span className="text-xs text-gray-500">answers only from the review shown here</span>
        </span>
        {open ? <ChevronUp className="h-4 w-4 text-gray-500" aria-hidden="true" /> : <ChevronDown className="h-4 w-4 text-gray-500" aria-hidden="true" />}
      </button>
      {open && (
        <div className="border-t border-gray-100 p-4">
          {messages.length > 0 && (
            <div className="mb-3 max-h-80 space-y-2 overflow-y-auto" aria-live="polite">
              {messages.map(m => (
                <div key={m.id} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] rounded-xl px-3 py-2 text-sm ${m.role === 'user' ? 'bg-gray-900 text-white' : 'border border-gray-200 bg-gray-50 text-gray-800'}`}>
                    {m.role === 'user' ? <p>{m.text}</p> : (
                      <div className="prose prose-sm max-w-none">
                        <ReactMarkdown remarkPlugins={[remarkGfm]} disallowedElements={DISALLOWED} unwrapDisallowed components={COMPONENTS}>{m.text}</ReactMarkdown>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {busy && <div className="flex justify-start"><div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2"><Loader2 className="h-4 w-4 animate-spin text-gray-500" aria-label="Thinking" /></div></div>}
              <div ref={endRef} />
            </div>
          )}
          <form onSubmit={e => { e.preventDefault(); send(); }} className="flex gap-2">
            <Input value={input} onChange={e => setInput(e.target.value)} placeholder="e.g. Why did Flipkart drop this week?" className="h-9 flex-1 text-sm" disabled={busy} aria-label="Your question" data-testid="review-ask-input" />
            <Button type="submit" size="sm" className="h-9" disabled={busy || !input.trim()} aria-label="Ask" data-testid="review-ask-send"><Send className="h-4 w-4" aria-hidden="true" /></Button>
          </form>
          {messages.length === 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {SUGGESTIONS.map(text => (
                <button key={text} type="button" onClick={() => send(text)} className="rounded-full bg-gray-100 px-2.5 py-1 text-xs text-gray-700 hover:bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">{text}</button>
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default AskPanel;
